'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';
import Image from 'next/image';

/** 히어로 배경 — 할리 실드(좌측 하단, 원본 비율 그대로) */
function HarleyHeroShield() {
  return (
    <div
      className="pointer-events-none absolute z-[6]"
      style={{
        left: 'clamp(0.75rem, 3vw, 2rem)',
        bottom: 'clamp(0.75rem, 3vh, 2rem)',
      }}
      aria-hidden
    >
      <div
        className="rounded-2xl bg-black/35 px-3 py-3 ring-1 ring-white/10 backdrop-blur-[2px]"
        style={{
          boxShadow:
            '0 2px 12px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
      >
        <Image
          src="/brands/harley-davidson-logo.png"
          alt=""
          width={440}
          height={560}
          sizes="(min-width: 1024px) 228px, 180px"
          className="h-auto max-h-[min(42vh,320px)] w-[clamp(108px,18vw,228px)] max-w-full object-contain"
          priority
        />
      </div>
    </div>
  );
}

/** 로그인 폼 상단 — `public/brands/ALW.png` (저장소 루트 `ALW.png` 와 동일 자산) */
function AwlGarageMark({ className }: { className?: string }) {
  return (
    <div
      className={`relative mx-auto flex w-full max-w-[min(100%,588px)] items-center justify-center ${className ?? ''}`}
      style={{ height: 'clamp(9.36rem, 24.336vw, 14.508rem)' }}
    >
      {/* Next/Image 최적화·fill 이 환경에 따라 깨지는 경우 대비 — public 정적 PNG 직접 참조 */}
      <img
        src="/brands/ALW.png"
        alt="AWL Garage"
        width={917}
        height={406}
        decoding="async"
        className="max-h-full w-auto max-w-full object-contain object-center"
        style={{
          filter: 'drop-shadow(0 8px 28px rgba(0,0,0,0.35))',
        }}
      />
    </div>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const { status } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (status === 'authenticated') {
      router.replace('/dashboard');
    }
  }, [status, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn('credentials', {
        email: email.trim(),
        password,
        redirect: false,
      });
      if (result?.error) {
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
        setIsLoading(false);
        return;
      }
      router.push('/dashboard');
      router.refresh();
    } catch {
      setError('인증 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex overflow-hidden"
      style={{ background: 'var(--bg-primary)' }}
    >
      {/* 왼쪽: 히어로 패널 */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/harley-bg.png"
            alt="Harley Davidson Custom Motorcycle"
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            priority
          />
          {/* 통합 그라데이션 (좌→우 어두워짐 + 하단 페이드) */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(115deg, rgba(11,12,16,0.20) 0%, rgba(11,12,16,0.10) 38%, rgba(11,12,16,0.78) 88%, rgba(11,12,16,0.92) 100%)',
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-40"
            style={{
              background:
                'linear-gradient(to top, rgba(11,12,16,0.96) 0%, transparent 100%)',
            }}
          />
        </div>

        {/* 좌측 콘텐츠 — 패널 우측 하단 */}
        <div
          className="relative z-10 flex h-full w-full items-end justify-end"
          style={{ padding: 'clamp(40px, 5vw, 80px)' }}
        >
          <div className="max-w-lg shrink-0 text-left">
            <div className="flex items-center gap-3 mb-7">
              <div className="h-px w-10" style={{ background: 'var(--hd-orange)' }} />
              <span
                className="text-[11px] font-semibold tracking-[0.18em] uppercase"
                style={{ color: 'var(--hd-orange)' }}
              >
                Premium Service
              </span>
            </div>

            <h1
              className="text-white"
              style={{
                fontFamily: "'Bebas Neue', 'Inter', sans-serif",
                fontSize: 'clamp(2.4rem, 4.6vw, 4rem)',
                lineHeight: 0.98,
                letterSpacing: '0.015em',
                textShadow: '0 6px 30px rgba(0,0,0,0.45)',
              }}
            >
              RIDE.
              <br />
              <span style={{ color: 'var(--hd-orange)' }}>CUSTOMIZE.</span>
              <br />
              DOMINATE.
            </h1>

            <p
              className="mt-5 text-[15px] max-w-sm"
              style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, letterSpacing: '-0.005em' }}
            >
              할리데이비슨 커스텀 &amp; 튜닝 전문점.
              <br />
              최고의 기술로 당신만의 라이딩을 완성합니다.
            </p>

            {/* 스탯 */}
            <div className="flex gap-10 mt-10">
              {[
                { value: '15+', label: '년 경력' },
                { value: '1,200+', label: '정비 완료' },
                { value: '98%', label: '고객 만족도' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    className="text-[1.6rem] tabular-nums"
                    style={{
                      fontWeight: 700,
                      color: 'var(--hd-orange)',
                      letterSpacing: '-0.025em',
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-[11px] mt-1.5"
                    style={{ color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <HarleyHeroShield />

        {/* 절제된 오렌지 글로우 */}
        <div
          className="absolute -bottom-24 -left-24 w-96 h-96 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,107,0,0.18) 0%, transparent 70%)',
            filter: 'blur(48px)',
          }}
        />
      </div>

      {/* 오른쪽: 로그인 패널 */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-6 py-14 sm:py-16 relative max-lg:pb-[max(3.5rem,env(safe-area-inset-bottom,0px))]"
        style={{ background: 'var(--login-panel-bg)' }}
      >
        <div className="w-full max-w-[400px]">
          {/* 브랜드 마크 */}
          <div className="flex justify-center mb-8 lg:mb-10">
            <AwlGarageMark />
          </div>

          {/* 헤더 */}
          <div className="mb-9 text-center">
            <div
              className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full"
              style={{
                background: 'rgba(255, 107, 0, 0.08)',
                border: '1px solid rgba(255, 107, 0, 0.22)',
              }}
            >
              <span
                className="text-[10.5px] font-semibold tracking-[0.14em] uppercase"
                style={{ color: 'var(--hd-orange)' }}
              >
                Management System
              </span>
            </div>
            <h2
              className="text-[1.625rem] mb-2 leading-tight"
              style={{
                color: 'var(--text-primary)',
                fontWeight: 700,
                letterSpacing: '-0.025em',
              }}
            >
              관리자 로그인
            </h2>
            <p
              className="text-[13.5px] leading-relaxed"
              style={{ color: 'var(--text-muted)', letterSpacing: '-0.005em' }}
            >
              할리데이비슨 정비센터 통합 관리 시스템
            </p>
          </div>

          {/* 로그인 폼 */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* 이메일 */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="email"
                className="text-[12.5px] font-medium"
                style={{ color: 'var(--text-secondary)', letterSpacing: '-0.005em' }}
              >
                이메일
              </label>
              <div className="relative">
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-soft)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력하세요"
                  required
                  autoComplete="email"
                  className="input-dark w-full"
                  style={{
                    fontSize: 14,
                    paddingLeft: 42,
                    paddingRight: 14,
                    height: 44,
                  }}
                />
              </div>
            </div>

            {/* 비밀번호 */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="password"
                className="text-[12.5px] font-medium"
                style={{ color: 'var(--text-secondary)', letterSpacing: '-0.005em' }}
              >
                비밀번호
              </label>
              <div className="relative">
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-soft)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  required
                  autoComplete="current-password"
                  className="input-dark w-full"
                  style={{
                    fontSize: 14,
                    paddingLeft: 42,
                    paddingRight: 42,
                    height: 44,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                  className="absolute"
                  style={{
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-soft)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 6,
                    borderRadius: 6,
                    display: 'inline-flex',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-soft)')}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                className="flex items-center gap-2 px-3.5 py-3 rounded-[10px] text-[13px]"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#FC8181',
                  letterSpacing: '-0.005em',
                }}
                role="alert"
              >
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {/* 로그인 버튼 */}
            <button
              id="login-submit-btn"
              type="submit"
              disabled={isLoading}
              className="btn-orange w-full mt-1"
              style={{
                height: 46,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.005em',
                opacity: isLoading ? 0.85 : 1,
              }}
            >
              {isLoading ? (
                <>
                  <div
                    className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white"
                    style={{ animation: 'rotate-slow 0.8s linear infinite' }}
                  />
                  인증 중...
                </>
              ) : (
                <>
                  로그인
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* 하단 */}
          <div
            className="flex items-center justify-between mt-10 pt-5"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <span className="text-[11.5px]" style={{ color: 'var(--text-soft)' }}>
              © 2026 정비센터 관리시스템
            </span>
            <div className="flex items-center gap-1.5">
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: 'var(--status-completed)', animation: 'pulse-orange 2s infinite' }}
              />
              <span className="text-[11.5px]" style={{ color: 'var(--text-soft)' }}>
                시스템 정상
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
